// Phase SIKA-5e/4 + 5g — Claude.ai-style SIKA Chat page (v2).
//
// Layout (5g):
//   [ AppShell sidebar ] [ SIKA secondary sidebar ] [ chat column ]
//                          (collapsible, persisted)   prose / pill / cards
//
// Wired components (all from src/pages/sika/chat-v2/):
//   - SikaChatSidebar     (5g) — secondary sidebar with brand, top nav
//                          (New / Search / Conversations / Goals /
//                          Active plan / Vault / Pinned), goals list,
//                          recent conversations (SikaHistoryRail), user
//                          chip. Collapsible to 52px icon rail.
//                          State persisted in localStorage.
//   - ChatTopBar          breadcrumb + mobile hamburger + Open Goal
//                          Layer CTA. Pre-5g had a History ▾ button;
//                          retired — sidebar owns history now.
//   - SikaMessageV2       role-branched prose / pill / inline card
//   - SikaComposer        floating bottom card + voice + tier pill
//   - GoalPopover         composer + button → goal switcher
//
// Hook: existing useSikaChat — SSE streaming, agent steps, sessions.
// HistorySlideover retired in 5g (replaced by sidebar).

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { useSikaChat } from "../../sika/useSikaChat.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { Icon } from "../../components/icons/Icon.tsx";

import { ChatTopBar } from "./chat-v2/ChatTopBar.tsx";
import { GoalPopover } from "./chat-v2/GoalPopover.tsx";
import { SikaChatSidebar } from "./chat-v2/SikaChatSidebar.tsx";
import { SikaComposer } from "./chat-v2/SikaComposer.tsx";
import { SikaMessageV2 } from "./chat-v2/SikaMessage.tsx";

const TERMINAL_STATUSES = new Set(["completed", "archived", "cancelled"]);
const COLLAPSED_KEY = "strikza:sika:chat-sidebar-collapsed";

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}
function writeCollapsed(v: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function SikaChatPageV2(): JSX.Element {
  const { user } = useAuthSession();
  const { isPro } = useUserTier();
  const { goals } = useSikaGoals();
  const chat = useSikaChat();

  const [searchParams] = useSearchParams();
  const [goalPopoverOpen, setGoalPopoverOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    readCollapsed(),
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);

  // Active goals (non-terminal) for sidebar + popover.
  const activeGoals = useMemo(
    () => goals.filter((g) => !TERMINAL_STATUSES.has(g.status)),
    [goals],
  );

  // URL ?goal=<id> wins on initial mount.
  const urlGoalId = searchParams.get("goal");
  useEffect(() => {
    if (urlGoalId && urlGoalId !== chat.goalId) {
      chat.setGoalId(urlGoalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlGoalId]);

  const activeGoal = useMemo(
    () =>
      activeGoals.find((g) => g.id === chat.goalId) ?? activeGoals[0] ?? null,
    [activeGoals, chat.goalId],
  );

  // Auto-scroll on new tokens / messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    const newMessage = chat.messages.length !== prevMessageCountRef.current;
    if (isNearBottom || newMessage) {
      el.scrollTop = el.scrollHeight;
    }
    prevMessageCountRef.current = chat.messages.length;
  }, [chat.messages.length, chat.streamingText, chat.sending]);

  // Weekly user-message count for free-tier soft cap.
  const weekUserMessageCount = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return chat.messages.filter(
      (m) =>
        m.role === "user" && new Date(m.created_at).getTime() >= sevenDaysAgo,
    ).length;
  }, [chat.messages]);

  const visible = chat.messages.filter(
    (m) => m.role !== "tool" || m.card_type != null,
  );

  const toggleSidebar = (): void => {
    setSidebarCollapsed((v) => {
      const next = !v;
      writeCollapsed(next);
      return next;
    });
  };

  return (
    <div className="sa-root" style={{ minHeight: "100%" }}>
      <div className="sika-chat-shell" data-page="sika-chat-v2">
        <SikaChatSidebar
          goals={goals}
          activeGoalId={chat.goalId}
          onSelectGoal={(id) => chat.setGoalId(id)}
          onNewConversation={() => chat.newSession()}
          activeSessionId={chat.sessionId}
          onSelectSession={(sid, gid) => chat.loadSession(sid, gid)}
          userEmail={user?.email ?? null}
          pro={isPro}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
        />

        <div className="sika-chat-2" data-testid="sika-chat-2-main">
          <ChatTopBar
            goalId={activeGoal?.id ?? null}
            goalTitle={activeGoal?.title ?? null}
            onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
            onNewConversation={() => chat.newSession()}
          />

          <div className="sc2-scroll" ref={scrollRef}>
            <div className="sc2-col" role="log" aria-label="SIKA conversation">
              {chat.loadingHistory ? (
                <div className="sc2-thinking">Loading conversation…</div>
              ) : visible.length === 0 && !chat.sending ? (
                <EmptyState
                  hasActiveGoal={!!activeGoal}
                  goalTitle={activeGoal?.title ?? null}
                />
              ) : (
                visible.map((m) => (
                  <SikaMessageV2
                    key={m.id}
                    msg={m}
                    onAction={(t) => void chat.send(t)}
                    sending={chat.sending}
                  />
                ))
              )}

              {chat.sending ? (
                <SikaMessageV2
                  msg={{
                    id: "__streaming__",
                    role: "assistant",
                    content: chat.streamingText,
                    tool_use:
                      chat.agentSteps.length > 0
                        ? { agent_steps: chat.agentSteps }
                        : null,
                    card_type: null,
                    created_at: new Date().toISOString(),
                  }}
                  streaming
                />
              ) : null}

              {chat.error ? (
                <div
                  className="sc2-msg-assistant"
                  style={{
                    border: "1px solid var(--sa-signal-red)",
                    borderRadius: 8,
                    padding: 14,
                    color: "var(--sa-signal-red)",
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 12.5,
                    lineHeight: 1.55,
                  }}
                  data-testid="sc2-error"
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Something went wrong reaching SIKA.
                  </div>
                  <div
                    style={{
                      color: "var(--sa-fg-secondary)",
                      wordBreak: "break-word",
                    }}
                    data-testid="sc2-error-detail"
                  >
                    {chat.error}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Composer pinned bottom. + button opens goal popover. */}
          <div style={{ position: "relative" }}>
            <SikaComposer
              onSend={(text) => void chat.send(text)}
              disabled={chat.sending}
              pro={isPro}
              weeklyUserMessageCount={weekUserMessageCount}
              placeholder={
                activeGoal
                  ? `Message SIKA about ${activeGoal.title}`
                  : "Message SIKA"
              }
              leftSlot={
                <button
                  type="button"
                  className="sc2-icon-btn"
                  onClick={() => setGoalPopoverOpen((v) => !v)}
                  aria-label="Switch goal"
                  data-testid="sc2-composer-plus"
                  disabled={chat.sending}
                >
                  <Icon name="plus" size={16} />
                </button>
              }
            />
            <div
              style={{
                position: "absolute",
                bottom: 70,
                left: 36,
                pointerEvents: goalPopoverOpen ? "auto" : "none",
              }}
            >
              <GoalPopover
                open={goalPopoverOpen}
                onClose={() => setGoalPopoverOpen(false)}
                goals={activeGoals}
                activeGoalId={activeGoal?.id ?? null}
                onSelect={(id) => chat.setGoalId(id)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  hasActiveGoal,
  goalTitle,
}: {
  hasActiveGoal: boolean;
  goalTitle: string | null;
}): JSX.Element {
  if (hasActiveGoal) {
    return (
      <div
        data-testid="sc2-empty-with-goal"
        style={{
          fontFamily: "var(--sa-font-body)",
          fontSize: 15,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.55,
          padding: "60px 0",
          textAlign: "center",
        }}
      >
        Ready when you are.
        <br />
        <span style={{ color: "var(--sa-fg-muted)" }}>
          {goalTitle ? `Ask SIKA about ${goalTitle}.` : "Start a conversation."}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="sc2-empty-no-goal"
      style={{
        fontFamily: "var(--sa-font-body)",
        fontSize: 15,
        color: "var(--sa-fg-secondary)",
        lineHeight: 1.55,
        padding: "60px 0",
        textAlign: "center",
      }}
    >
      What are you working toward?
      <br />
      <span style={{ color: "var(--sa-fg-muted)" }}>
        Start by telling SIKA your goal — it'll build the plan from there.
      </span>
    </div>
  );
}
